/* eslint-disable no-shadow */
export interface Account {
  _id: string;
  sfId: string;
  zuoraId: string;
  name: string;
  globalAccountName: string;
  locationSfId: string;
  locationId: string;
  autopay: boolean;
  currencyCode: string;
  defaultPaymentMethodId: string;
  paymentGateway: string;
  moveOutDate: string;
  timezone?: string;
  billToContact?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  accountNumber?: string;
  balance?: number;
  unappliedBalance?: number;
  unappliedCreditMemoAmount?: number;
  accountOpeningBalanceSD?: number;
  securityDeposit?: {
    lastSubmittedBy?: string;
    lastSubmittedAt?: string;
    refundStatus?: SecurityDepositRefundStatus;
    bankInfoSubmitted?: boolean;
    bankDocumentSubmitted?: boolean;
    bankDocumentFileName?: string;
    dateClosed?: string;
    refundAmount?: number;
    internalNotes?: string;
    last4Digits?: string;
    refundInfoRequestEmailTriggeredAt?: string;
  };
  invoicingSystemType?: InvoicingSystemTypes;
}

export enum InvoicingSystemTypes {
  StripePayment = "StripePayment",
  StripeBilling = "StripeBilling",
  Zuora = "Zuora",
}

export enum ZuoraPaymentMethodTypes {
  ACH = "ach",
  Bacs = "Bacs",
  CreditCard = "credit-card",
  Pad = "PAD",
  Sepa = "SEPA",
}

export enum StripePaymentMethodTypes {
  CreditCard = "card",
  ACH = "us_bank_account",
  Bacs = "bacs_debit",
  Sepa = "sepa_debit",
  Pad = "acss_debit",
}

export enum SecurityDepositRefundStatus {
  Open = "open",
  Processing = "processing",
  Reviewed = "reviewed",
  Approved = "approved",
  OnHold = "on-hold",
  Canceled = "canceled",
  Paid = "paid",
  IncorrectBankInfo = "incorrect-bank-info",
}

export type ZuoraPaymentMethod =
  | PaymentMethodACH
  | PaymentMethodBacs
  | PaymentMethodSepa
  | PaymentMethodCreditCard
  | PaymentMethodPad;

export enum StripeCreditCardBrands {
  Mastercard = "mastercard",
  Visa = "visa",
  Amex = "amex",
  Discover = "discover",
}

export type StripePaymentMethodBase = {
  id: string;
  customerId: string;
  default: boolean;
  createdAt: string;
};

export type StripeCard = StripePaymentMethodBase & {
  type: StripePaymentMethodTypes.CreditCard;
  cardNumber: string;
  cardType: StripeCreditCardBrands;
  expMonth: number;
  expYear: number;
};

export type StripeACH = StripePaymentMethodBase & {
  type: StripePaymentMethodTypes.ACH;
  bankABACode?: string | null;
  bankAccountNumber?: string | null;
  bankAccountType?: string | null;
  bankName?: string | null;
  bankAccountName?: string | null;
};

export type StripePad = StripePaymentMethodBase & {
  type: StripePaymentMethodTypes.Pad;
  bankName?: string | null;
  bankABACode?: string | null;
  bankAccountNumber?: string | null;
};

export type StripeBacs = StripePaymentMethodBase & {
  type: StripePaymentMethodTypes.Bacs;
  bankName?: string | null;
  bankABACode?: string | null;
  bankAccountNumber?: string | null;
  bankCode?: string | null;
};

export type StripeSepa = StripePaymentMethodBase & {
  type: StripePaymentMethodTypes.Sepa;
  bankName?: string | null;
  bankAccountNumber?: string | null;
  bankABACode?: string | null;
};

export type StripePaymentMethod =
  | StripeCard
  | StripeACH
  | StripePad
  | StripeBacs
  | StripeSepa;

export interface PaymentMethodBase {
  id: string;
  status: string;
  default: boolean;
  createdAt: string;
  updatedAt: string;
  nickname: string | null;
}

export interface PaymentMethodACH extends PaymentMethodBase {
  type: ZuoraPaymentMethodTypes.ACH;
  bankABACode: string;
  bankAccountNumber: string;
  bankAccountType: string;
  bankName: string;
  bankAccountName: string;
  accountHolderInfo: AccountHolderInfo;
}

export interface PaymentMethodCreditCard extends PaymentMethodBase {
  type: ZuoraPaymentMethodTypes.CreditCard;
  cardNumber: string;
  cardType: CardTypes;
  expMonth: number;
  expYear: number;
  accountHolderInfo: AccountHolderInfo;
}

export interface PaymentMethodPad extends PaymentMethodBase {
  type: ZuoraPaymentMethodTypes.Pad;
  accountNumber: string;
  bankCode: string;
  branchCode: string;
  mandateInfo: unknown;
  lastTransaction: string;
  useDefaultRetryRule: boolean;
  bankIdentificationNumber: null;
  deviceSessionId: null;
  existingMandate: null;
  ipAddress: null;
  lastFailedSaleTransactionDate: null;
  lastTransactionDateTime: string;
  lastTransactionStatus: string;
  maxConsecutivePaymentFailures: null;
  numConsecutiveFailures: number;
  paymentRetryWindow: null;
  totalNumberOfProcessedPayments: number;
  totalNumberOfErrorPayments: number;
  createdDate: string;
  updatedDate: string;
  isDeleted: boolean;
  accountId: string;
  accountHolderInfo: AccountHolderInfo;
}

export interface PaymentMethodBacs extends PaymentMethodBase {
  type: ZuoraPaymentMethodTypes.Bacs;
  bankTransferType: "DirectDebitUK";
  IBAN: string;
  businessIdentificationCode: string | null;
  accountNumber: string;
  bankCode: string;
  branchCode: string | null;
  identityNumber: string | null;
  accountKey: string;
  lastTransaction: string;
  useDefaultRetryRule: boolean;
  bankIdentificationNumber: string | null;
  deviceSessionId: string | null;
  existingMandate: string | null;
  ipAddress: string | null;
  lastFailedSaleTransactionDate: string | null;
  lastTransactionDateTime: string;
  lastTransactionStatus: string | null;
  maxConsecutivePaymentFailures: string | null;
  numConsecutiveFailures: number;
  paymentRetryWindow: string | null;
  totalNumberOfProcessedPayments: number;
  totalNumberOfErrorPayments: number;
  createdDate: string;
  updatedDate: string;
  accountHolderInfo: AccountHolderInfo;
}

export interface PaymentMethodSepa extends PaymentMethodBase {
  type: ZuoraPaymentMethodTypes.Sepa;
  bankTransferType: "SEPA";
  IBAN: string;
  accountNumber: string;
  identityNumber: string | null;
  accountKey: string;
  lastTransaction: string;
  useDefaultRetryRule: boolean;
  bankIdentificationNumber: string | null;
  deviceSessionId: string | null;
  existingMandate: string | null;
  ipAddress: string | null;
  lastFailedSaleTransactionDate: string | null;
  lastTransactionDateTime: string;
  lastTransactionStatus: string | null;
  maxConsecutivePaymentFailures: string | null;
  numConsecutiveFailures: number;
  paymentRetryWindow: string | null;
  totalNumberOfProcessedPayments: number;
  totalNumberOfErrorPayments: number;
  createdDate: string;
  updatedDate: string;
  accountHolderInfo: AccountHolderInfo;
}

export interface AccountHolderInfo {
  accountHolderName?: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  country?: string;
  email: string;
  phone?: string;
  state: string;
  zipCode: string;
}

export enum CardTypes {
  Visa = "visa",
  Amex = "american-express",
  Mastercard = "master-card",
  Discover = "discover",
}
export interface Invoice {
  id: string;
  createdAt: string;
  invoiceNumber: string;
  amount: number;
  creditMemoAmount: number;
  currencyCode: string;
  dueDate: string;
  balance: number;
  paymentStatus: PaymentStatus;
  invoiceDate?: string;
  items?: InvoiceItem[];
  amountWithoutTax: number;
}

export interface InvoiceItem {
  appliedToItemId: string;
  availableToCreditAmount: number;
  balance: number;
  chargeAmount: number;
  chargeDate: string;
  chargeDescription: string;
  chargeId: string;
  chargeName: string;
  chargeType: string;
  description: string;
  id: string;
  processingType: string;
  productName: string;
  productRatePlanChargeId: string;
  quantity: number;
  serviceEndDate: string;
  serviceStartDate: string;
  sku: string;
  soldToContactId: string;
  soldToContactSnapshotId: string;
  sourceItemType: string;
  subscriptionId: string;
  subscriptionName: string;
  taxAmount: number;
  unitOfMeasure: string;
  unitPrice: number;
}

export interface AccountSignature {
  signature: string;
  token: string;
  tenantId: string;
  publicKey: string;
  paymentPageId: string;
  paymentPageUrl: string;
}

export interface DecryptSignature {
  signature: string;
  token: string;
  tenantId: string;
  publicKey: string;
  paymentPageId: string;
  paymentPageUrl: string;
}

export enum PaymentStatus {
  Failed = "failed",
  Paid = "paid",
  PaymentDue = "payment-due",
  Processing = "processing",
  Voided = "voided",
}

export enum PaymentErrorMessages {
  NotValidated = "The provided PaymentMethod cannot be attached",
}

export interface Location {
  _id: string;
  name: string;
  currencyCode: string;
  creditCardFeeRate: number;
  timezone: string;
  locationCode: string;
  stripeAccountId?: string;
  bankAccountAchId?: string;
  bankAccountName?: string;
  zuoraAccountId?: string;
  licensor: string | null;
  address: string;
  address2?: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  createdAt: string;
  updatedAt: string;
  email?: string;
  locationsLegalEntityName?: string;
}

export enum OrderStatus {
  Completed = "Completed",
  Pending = "Pending",
  Processing = "Processing",
}

export enum AddonRatePlanChargeModel {
  FlatFee = "Flat Fee Pricing",
  PerUnit = "Per Unit Pricing",
  Volume = "Volume Pricing",
  Tiered = "Tiered Pricing",
  Overage = "Overage Pricing",
  TieredwithOverage = "Tiered with Overage Pricing",
  DiscountFixedAmount = "Discount-Fixed Amount",
  DiscountPercentage = "Discount-Percentage",
}

export interface Order {
  proposalId: string;
  locationId: string;
  account: {
    name: string;
    id?: string;
    sfId: string;
  };
  quoteSfId?: string;
  currency: string;
  status: OrderStatus;
  opportunity: {
    sfId: string;
    status: string;
    stage: string;
  };
  primaryContact: {
    firstName: string;
    lastName: string;
    email: string;
    sfId: string;
  };
  agreement: {
    companyName: string;
    contactEmail: string;
    contactName: string;
    signedAt: Date;
    signedBy: string;
  };
  terms: {
    startDate: string; // YYYY-MM-DD
    endDate: string; // YYYY-MM-DD
    termLength?: number | null;
    totalTermLength?: number | null;
    promotion?: string;
  };
  totals: {
    price: number;
    oneTimeFeesAmount?: number;
    securityDepositAmount?: number;
  };
  zuora?: {
    accountId: string;
    orderId: string;
    subscriptionId: string;
  };
  offices: {
    officeId: string;
    productName: string;
    productSfId: string;
    totalPrice: number;
    nbOfSeats?: number | null;
    zuoraProductRatePlanId: string;
    zuoraProductRatePlanChargeId: string;
    deposit: {
      amount: number;
      zuoraProductRatePlanChargeId: string;
    };
  }[];
  memberships: {
    membershipId: string;
    productSfId: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    zuoraProductRatePlanId: string;
    zuoraProductRatePlanChargeId: string;
  }[];
  addons: {
    addonId: string;
    productSfId: string;
    chargeSfId: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    billingFrequency: string;
    zuoraProductRatePlanId: string;
    zuoraProductRatePlanChargeId: string;
    zuoraRatePlanChargeModel: AddonRatePlanChargeModel;
  }[];
}

export enum InvoiceEndpointType {
  INVOICES = "invoices", // Zuora Invoices
  REPORTING_INVOICES = "reporting-invoices", // Stripe Invoices
}
